import { BCReportViewer } from '@bimser/components';
import { FormItemTypes, IDictionary } from '@bimser/core';
import objectHash from 'object-hash';
import { JSX } from 'react';
import { ServiceHelper } from '@bimser/studio-core';
import { IPropertyField } from '../../../../entities/common';
import { getActionValue } from '../../../../sagas/ruleValidation/action';
import Control from '../../base/Control';
import VisualControl from '../../base/VisualControl';
import { IPropertyChangedEventArgs } from '../../entities/EventArgs';
import { IVisualControlProperties } from '../../entities/IVisualControlProperties';
import { IReportViewerParameters, IReportViewerProperties } from './entities';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import { HTTPRequest } from '@bimser/common';

export default class ReportViewer extends VisualControl {
	reportId: string;
	reportName: string;
	parameters: Array<IReportViewerParameters>;
	height: number;
	_parametersKey: string;
	__parameterValues: IDictionary<any>;

	constructor() {
		super();

		this.controlType = FormItemTypes.EControlTypes.ReportViewer;
		this.__parameterValues = {};
		this.height = 700;
	}

	__onLoad() {
		super.__onLoad();
		this.__setReportParameters();
		this.__subscribeProperties();
	}

	__subscribeProperties() {
		this.parameters?.forEach(parameter => {
			const fieldArr = parameter.path.split('.'),
				controlId = fieldArr.shift(),
				propertyPath = fieldArr.join('.');

			const control = (this.__form as any)[controlId] as Control;

			if (control) {
				control.subscribe(
					'event:OnPropertyChanged',
					(args: IPropertyChangedEventArgs<any>) => {
						if (propertyPath === args.name) {
							this.__setReportParameters();
						}
					}
				);
			}
		});
	}

	async __setReportParameters() {
		if (!this.__isInitialized && !this.__form.serviceUrl) {
			return;
		}

		this.__proxyChanging.loading = true;

		const getParameterValues = () => {
			const params = {};
			this.parameters?.forEach(parameter => {
				const _parameter = { ...parameter };
				const paramFieldSplit = _parameter.path.split('.');
				const cId = paramFieldSplit[0];
				paramFieldSplit.shift();
				const data = _get(this.__form, 'items', null);
				const keys = paramFieldSplit.join('.');
				if (cId && keys && data) {
					const val = getActionValue(cId, keys, data, { ignoreValueSeparator: true });
					params[parameter.name] = val;
				}
			});

			this.__parameterValues = _cloneDeep(params);

			return params;
		};

		const args: any = {
			projectName: this.__form.projectName,
			formName: this.__form.formName,
			viewName: this.__form.activeView,
			serviceUrl: this.__form.serviceUrl,
			reportId: this.reportId,
			parameters: getParameterValues()
		};

		await this.set('loading', true);

		ServiceHelper.setReportParameters(args)
			.then(async result => await this.set('_parametersKey', result))
			.catch(error => console.warn('ReportViewer: ', error))
			.finally(async () => await this.set('loading', false));
	}

	__loadProperties(data: IReportViewerProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.reportId = data.reportId;
		this.__proxyChanged.reportName = data.reportName;
		this.__proxyChanged.parameters = data.parameters;
		this.__proxyChanged.height = data.height;
		this.__proxyChanged._parametersKey = data._parametersKey;
	}

	__getComponentData(): IReportViewerProperties {
		return {
			...(super.__getComponentData() as IVisualControlProperties),
			reportId: this.reportId,
			reportName: this.reportName,
			parameters: this.parameters,
			height: this.height
		};
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'reportId',
				type: 'string'
			},
			{
				name: 'reportName',
				type: 'string'
			},
			{
				name: 'parameters',
				type: 'array'
			},
			{
				name: 'height',
				type: 'number'
			}
		];
	}

	__render(children?: JSX.Element) {
		return super.__render(
			<BCReportViewer
				projectName={this.__form.projectName}
				reportId={this.reportId}
				reportName={this.reportName}
				parametersKey={this._parametersKey}
				serviceUrl={this.__form.serviceUrl}
				parametersHash={objectHash(this.__parameterValues)}
				disabled={this.__isDisabled()}
				ajaxHeaders={HTTPRequest.defaultHeaders}
			/>
		);
	}
}
